import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BarsTransition } from '@tanium/react-loading-transition';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormSection, FormRow } from '@tanium/react-form';
import get from 'lodash.get';
import { Field, FieldProps, FormikErrors, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQueryParam, StringParam } from 'use-query-params';
import { useTheme } from '@tanium/react-theme-context';
import { Stack } from '@tanium/react-stack';
import { Box } from '@tanium/react-box';
import EosServiceSelectors from 'components/pages/eosService/store/EosServiceSelectors';
import EosServiceSlice from 'components/pages/eosService/store/EosServiceSlice';
import IDropdownOption from 'common/IDropdownOption';
import {
  TARGET_ID,
  TARGET_NAME,
} from 'components/pages/request/requestPartials/createRequestFieldsPartials/Environments';
import { ACCESS_TYPE } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import { ACCESS_TYPES } from 'components/pages/request/CreateRequestPage';
import { isProcessing } from 'store/ActionState';
import Tooltip from 'components/tooltip/Tooltip';

export const EosServiceEnvironment = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formik = useFormikContext();
  const theme = useTheme();
  const eosServices = useSelector(EosServiceSelectors.selectEosServices);
  const selectFetchServicesState = useSelector(EosServiceSelectors.selectEosServiceState);
  const [eosServiceOptions, setEosServiceOptions] = useState<IDropdownOption[]>([]);
  const [targetIdQuery, setTargetIdQuery] = useQueryParam(TARGET_ID, StringParam);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setAccessTypeQuery] = useQueryParam(ACCESS_TYPE, StringParam);

  useEffect(() => {
    dispatch(EosServiceSlice.actions.listEosServices({}));
  }, [dispatch]);

  useEffect(() => {
    if (eosServices && eosServices.length) {
      setEosServiceOptions(eosServices.map((s) => ({ label: s.name, value: s.id, description: s.description })));
    }
  }, [setEosServiceOptions, eosServices]);

  // Ran on initialization (with making sure EosServices have a non-empty array)
  useEffect(() => {
    if (targetIdQuery && eosServices && eosServices.length) {
      void formik.setFieldValue(TARGET_ID, targetIdQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eosServices]);

  const formInputIdPrefix = 'create-request-';
  return (
    <FormSection title={t('SELECT_SERVICE')}>
      <Field name={TARGET_ID} data-testid={TARGET_ID}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('EOS_SERVICE')}
            required
            description={t('EOS_SERVICE_INFO')}
            errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
            labelId={`${formInputIdPrefix}${field.name}`}
          >
            {<BarsTransition isLoading={isProcessing(selectFetchServicesState)} />}
            {!isProcessing(selectFetchServicesState) && eosServices && (
              <FormSelectInput
                placeholder={t('EOS_SERVICE_PLACEHOLDER')}
                onChange={(option): void => {
                  void formik.setFieldValue(TARGET_ID, option.value);
                  void formik.setFieldValue(TARGET_NAME, option.label);
                  void formik.setFieldValue(ACCESS_TYPE, '');
                  void formik.setFieldValue(ACCESS_TYPES, []);
                  setTargetIdQuery(option.value);
                  setAccessTypeQuery(undefined);
                }}
                value={eosServiceOptions.find((option) => {
                  return option.value === field.value;
                })}
                inputId={`${formInputIdPrefix}${field.name}`}
                options={eosServiceOptions}
                aria-label={t('EOS_SERVICE')}
                formatOptionLabel={(
                  option: {
                    value: string;
                    label: string;
                    description: string;
                  },
                  state
                ) => {
                  const isSelected =
                    state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                  /* istanbul ignore next */
                  const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                  return (
                    <Stack direction="column" itemSpacing={0.5}>
                      <Box>{option.label}</Box>
                      <Tooltip content={option.description}>
                        <Box color={subtextColor} fontSize={11}>
                          {option.description}
                        </Box>
                      </Tooltip>
                    </Stack>
                  );
                }}
                styles={{
                  singleValue: (base) => ({
                    ...base,
                    marginTop: theme.space[1],
                    marginBottom: theme.space[1],
                    position: 'relative',
                    transform: 'none',
                    whiteSpace: 'normal',
                    maxHeight: '3.75em',
                  }),
                  input: (base) => ({
                    ...base,
                    position: 'absolute',
                    top: theme.space[1] - 2,
                    paddingTop: 0,
                  }),
                }}
              />
            )}
          </FormRow>
        )}
      </Field>
    </FormSection>
  );
};

export default EosServiceEnvironment;
